import React, { useEffect, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Flame, TrendingUp, Share2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Top Shared Verses — social-proof leaderboard shown on the landing page.
 * Pulls aggregated counts from /api/shares/top and renders a ranked list.
 */
const RANK_COLORS = [
  'from-amber-400 to-amber-600',
  'from-slate-300 to-slate-500',
  'from-amber-700 to-amber-900',
];

const TopSharedVerses = ({ inFeed = false }) => {
  const [period, setPeriod] = useState('week');
  const [verses, setVerses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/shares/top?period=${period}&limit=6`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (!cancelled) setVerses(Array.isArray(data) ? data : []);
      } catch (_) {
        if (!cancelled) setVerses([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [period]);

  const periods = [
    { id: 'day', label: 'Today' },
    { id: 'week', label: 'This week' },
    { id: 'month', label: 'This month' },
    { id: 'all', label: 'All time' },
  ];

  return (
    <section
      id="top-shared-section"
      className={
        inFeed
          ? 'relative rounded-2xl bg-slate-900/40 border border-slate-800 p-5 sm:p-6'
          : 'relative py-24 sm:py-32 bg-gradient-to-b from-slate-950 to-purple-950/20'
      }
      data-testid="top-shared-section"
    >
      <div className={inFeed ? '' : 'container mx-auto px-4 sm:px-6 lg:px-8'}>
        <div className={inFeed ? '' : 'max-w-5xl mx-auto'}>
          {/* Heading */}
          <div className={`text-center space-y-3 ${inFeed ? 'mb-5' : 'mb-10 sm:mb-14'}`}>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-purple-500/10 border border-purple-400/20">
              <Flame className="w-4 h-4 text-amber-400" />
              <span className="text-xs sm:text-sm font-semibold text-amber-200 tracking-wide uppercase">
                Shared by the community
              </span>
            </div>
            <h2 className={`font-serif font-bold text-white ${inFeed ? 'text-2xl sm:text-3xl' : 'text-3xl sm:text-4xl lg:text-5xl'}`}>
              Most shared scriptures
            </h2>
            {!inFeed && (
              <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto">
                What is the Spirit speaking to Lumera readers right now? These are the verses people felt
                compelled to share this week.
              </p>
            )}
          </div>

          {/* Period pills */}
          <div
            className="flex flex-wrap justify-center gap-2 mb-8"
            data-testid="top-shared-period-tabs"
          >
            {periods.map((p) => (
              <button
                key={p.id}
                onClick={() => setPeriod(p.id)}
                data-testid={`top-shared-period-${p.id}`}
                className={`px-4 py-2 text-sm rounded-full border transition-all ${
                  period === p.id
                    ? 'bg-amber-500 text-slate-900 border-amber-500 shadow-lg shadow-amber-500/20'
                    : 'bg-slate-900/40 text-slate-300 border-slate-700 hover:border-amber-400/40'
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>

          {/* Leaderboard */}
          {loading ? (
            <div className="flex justify-center py-16" data-testid="top-shared-loading">
              <Loader2 className="w-8 h-8 animate-spin text-purple-300" />
            </div>
          ) : verses.length === 0 ? (
            <div className="text-center py-12" data-testid="top-shared-empty">
              <Share2 className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">
                No shares yet for this period — be the first to share a verse!
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-4" data-testid="top-shared-list">
              {verses.map((v, i) => {
                const rank = i + 1;
                const badge = RANK_COLORS[i] || 'from-purple-500 to-blue-500';
                return (
                  <Card
                    key={v.verse_reference}
                    className="relative bg-slate-900/60 border-slate-800 hover:border-amber-400/30 transition-all overflow-hidden"
                    data-testid={`top-shared-card-${rank}`}
                  >
                    <div className={`absolute top-0 left-0 w-1 h-full bg-gradient-to-b ${badge}`} />
                    <CardContent className="p-5 sm:p-6 space-y-2">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span
                            className={`inline-flex items-center justify-center w-9 h-9 rounded-full bg-gradient-to-br ${badge} text-slate-900 font-bold text-sm shadow`}
                          >
                            {rank}
                          </span>
                          <span className="text-amber-200 font-semibold text-sm sm:text-base">
                            {v.verse_reference}
                          </span>
                        </div>
                        <div className="flex items-center gap-1.5 text-slate-300 text-xs sm:text-sm">
                          <TrendingUp className="w-3.5 h-3.5 text-emerald-400" />
                          <span className="font-semibold">{v.count}</span>
                          <span className="text-slate-500">shares</span>
                        </div>
                      </div>
                      <p className="text-sm sm:text-base text-slate-300 leading-relaxed line-clamp-3 italic">
                        &ldquo;{v.verse_text}&rdquo;
                      </p>
                      {v.translation && (
                        <p className="text-xs text-slate-500 uppercase tracking-wide">
                          {v.translation}
                        </p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          <p className="text-center text-xs sm:text-sm text-slate-500 mt-8">
            Updated in real time • Powered by the Lumera community
          </p>
        </div>
      </div>
    </section>
  );
};

export default TopSharedVerses;
